import React, { useRef, useEffect } from "react";

import { styled } from "@mui/material/styles";
import { Track } from "twilio-video";

import { IVideoTrack } from "../../../types";
import useMediaStreamTrack from "../hooks/useMediaStreamTrack/useMediaStreamTrack";

const Video = styled("video")({
  width: "100%",
  height: "100%",
});

interface VideoTrackProps {
  videoId?: string;
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: Track.Priority | null;
}

export default function VideoTrack({ videoId, track, isLocal, priority }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const mediaStreamTrack = useMediaStreamTrack(track);

  useEffect(() => {
    const el = ref.current;

    el.muted = true;
    if (track.setPriority && priority) {
      track.setPriority(priority);
    }
    track.attach(el);

    return () => {
      track.detach(el);

      // This addresses a Chrome issue where the number of WebMediaPlayers is limited.
      // See: https://github.com/twilio/twilio-video.js/issues/1528
      el.srcObject = null;

      if (track.setPriority && priority) {
        // Passing `null` to setPriority will set the track's priority to that which it was published with.
        track.setPriority(null);
      }
    };
  }, [track, priority]);

  // The local video track is mirrored if it is not facing the environment.
  const isFrontFacing = mediaStreamTrack?.getSettings().facingMode !== "environment";
  const style = {
    transform: isLocal && isFrontFacing ? "rotateY(180deg)" : "",
    borderRadius: "8px",
    objectFit: "contain",
  } as React.CSSProperties;

  return <Video id={videoId} ref={ref} style={style} />;
}
